import React from 'react';

import { usePriceRowContext } from 'src/areas/product/components/price-list/price-row-context';
import { useAppContext } from '@prisjakt/ui-app-context';

import {
  CaptionText,
  FontColor,
  FontEmphasis,
  FontWeight,
} from '@prisjakt/ui/text/variants';
import { ShippingLabel } from '../shipping/shipping-label';
import { getCompiledPriceData } from './price-data';

import { PriceDisplay } from 'src/components/price-list/types';
import { PricePrice, CampaignMediaColors, Maybe, OfferPrices } from 'src/types';

import {
  StyledBadge,
  StyledMainPrice,
  StyledOfferPrice,
  StyledPriceLabel,
  StyledPriceWrapper,
  StyledSecondaryPrice,
} from './styles';

interface IPriceLabel {
  price?: PricePrice | null;
  offerPrice?: OfferPrices | null;
  priceDisplay: PriceDisplay;
  campaignColors?: Maybe<CampaignMediaColors>;
}

export const OfferPriceLabel: React.FC<React.PropsWithChildren<
  React.PropsWithChildren<IPriceLabel>
>> = props => {
  const { campaignColors, offerPrice, price, priceDisplay } = props;

  const { t, market } = useAppContext();
  const { isFeatured = false, forceMobileView } = usePriceRowContext();

  if (!price) {
    return null;
  }

  const priceData = getCompiledPriceData(
    price,
    offerPrice,
    priceDisplay,
    isFeatured,
    t,
    market
  );

  return (
    <StyledOfferPrice>
      {priceData.label && (
        <>
          {priceData.isMemberPrice ? (
            <StyledBadge color="YELLOW_INVERTED" text={priceData.label} />
          ) : (
            <CaptionText emphasis={FontEmphasis.MEDIUM}>
              {priceData.label}
            </CaptionText>
          )}
        </>
      )}
      <StyledPriceWrapper>
        <StyledMainPrice>
          {priceData.mainPrice && (
            <StyledPriceLabel
              value={priceData.mainPrice}
              emphasis={isFeatured ? FontEmphasis.HIGH : FontEmphasis.LOW}
              fontColor={campaignColors ? FontColor.NEGATIVE : undefined}
              weight={FontWeight.HEAVIEST}
            />
          )}
        </StyledMainPrice>

        {priceData.secondaryPrice && (
          <StyledSecondaryPrice
            value={priceData.secondaryPrice}
            emphasis={FontEmphasis.LOW}
          />
        )}
      </StyledPriceWrapper>

      {price && isFeatured && !forceMobileView && (
        <ShippingLabel
          emphasis={FontEmphasis.HIGH}
          price={price}
          priceDisplay={priceDisplay}
        />
      )}
    </StyledOfferPrice>
  );
};
